import React, { useState, useEffect, useRef } from "react";
import { Button, Dropdown, Icon, Message } from "semantic-ui-react";
import classNames from "classnames";
import { map, filter, groupBy, sortBy, size, uniq, compact } from "lodash";
import { HeaderPage } from "../../components/Admin";
import {
  useOrder,
  useCategory,
  useNow,
  useBeep,
  usePolling,
  BEEP_NEW_ORDER,
} from "../../hooks";
import { ORDER_STATUS } from "../../utils/constants";
import { formatElapsed } from "../../utils/date";
import { getTableNumber, getTablePlace } from "../../utils/table";
import "./KitchenScreen.scss";

// Pantalla de cocina por área (reemplaza el WhatsApp mozo → cocina). Pensada
// para una tablet/TV fija en cada área: muestra los pedidos PENDING de su área
// agrupados en comandas, y cocina toca "Listo" cuando sale el plato (pasa a
// READY; el mozo lo ve en la mesa y lo marca Entregado al servirlo).

const POLL_MS = 5000;
// Minutos de espera a partir de los cuales la comanda se pinta amarilla / roja.
const WARN_MINUTES = 10;
const LATE_MINUTES = 20;
// Pedidos de la misma mesa separados por más que esto = comandas distintas
// (ej. una segunda ronda media hora después).
const BATCH_GAP_MS = 2 * 60 * 1000;
// Cuánto se sigue viendo el aviso de un pedido que el mozo canceló o sirvió
// él mismo (ej. una bebida), en vez de que desaparezca sin explicación.
const NOTICE_VISIBLE_MS = 60 * 1000;
// Áreas elegidas en la pantalla; lista vacía = todas las áreas.
const AREA_STORAGE_KEY = "kitchenArea";

const normalizeArea = (area) => (area || "").trim().toLowerCase();
const getOrderArea = (order) =>
  order.product_data?.category_data?.production_area || "";

// Se guarda como lista JSON. Antes se guardaba un solo nombre de área (o
// "__all__"): ese valor viejo se sigue entendiendo.
function readStoredAreas() {
  try {
    const stored = localStorage.getItem(AREA_STORAGE_KEY);
    if (!stored || stored === "__all__") return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch (error) {
    return [];
  }
}

// Dos pedidos iguales (mismo plato, nota, opción, adicionales) se muestran en
// una sola línea "2 ×".
const lineKey = (order) =>
  JSON.stringify([
    order.product,
    order.note,
    order.option_choice,
    order.extras,
    order.offer,
    order.takeaway,
  ]);

// Comandas: por mesa, y dentro de la mesa se corta cuando pasan más de
// BATCH_GAP_MS entre un pedido y el siguiente.
function buildTickets(orders) {
  const byTable = groupBy(sortBy(orders, "created_at"), (order) =>
    getTableNumber(order)
  );
  const tickets = [];
  Object.keys(byTable).forEach((tableKey) => {
    let current = null;
    byTable[tableKey].forEach((order) => {
      const time = new Date(order.created_at).getTime();
      if (!current || time - current.lastTime > BATCH_GAP_MS) {
        current = {
          key: `${tableKey}-${order.id}`,
          table: tableKey,
          place: getTablePlace(order),
          orders: [],
          firstTime: time,
          lastTime: time,
        };
        tickets.push(current);
      }
      current.orders.push(order);
      current.lastTime = time;
    });
  });
  return sortBy(tickets, "firstTime").map((ticket) => ({
    ...ticket,
    lines: map(groupBy(ticket.orders, lineKey), (lineOrders) => lineOrders),
  }));
}

export function KitchenScreen() {
  const { getOpenOrders, markOrderReady } = useOrder();
  const { categories, getCategories } = useCategory();
  const now = useNow(15000);
  const [area, setArea] = useState(readStoredAreas);
  const [openOrders, setOpenOrders] = useState(null);
  const [offline, setOffline] = useState(false);
  const [notices, setNotices] = useState([]);
  // Pedido nuevo = pitido. Se desbloquea solo con el primer toque en la
  // pantalla (ver hooks/useBeep.js); el botón queda de respaldo.
  const { soundOn, beep, testBeep } = useBeep(BEEP_NEW_ORDER);
  const previousPendingRef = useRef(null);
  const areaRef = useRef(area);
  areaRef.current = area;

  useEffect(() => {
    getCategories();
  }, []);

  const inArea = (order, selectedAreas) =>
    size(selectedAreas) === 0 ||
    selectedAreas.some(
      (name) => normalizeArea(getOrderArea(order)) === normalizeArea(name)
    );

  const load = async () => {
    try {
      const response = await getOpenOrders();
      setOffline(false);
      setOpenOrders(response);

      const pending = filter(
        response,
        (order) =>
          order.status === ORDER_STATUS.PENDING && inArea(order, areaRef.current)
      );
      const previous = previousPendingRef.current;
      if (previous) {
        const byId = Object.fromEntries(response.map((order) => [order.id, order]));
        // Estaba pendiente y dejó de estarlo sin que cocina tocara "Listo":
        // - ya no existe entre los abiertos → lo cancelaron (o liberaron la
        //   mesa): se muestra tachado, cocina puede estar preparándolo.
        // - pasó a Entregado → el mozo lo sirvió él mismo (ej. una bebida).
        // Así no desaparece de la pantalla sin explicación.
        const at = Date.now();
        const newNotices = [];
        Object.values(previous).forEach((order) => {
          const current = byId[order.id];
          if (!current) newNotices.push({ order, at, kind: "cancelled" });
          else if (current.status === ORDER_STATUS.DELIVERED)
            newNotices.push({ order, at, kind: "delivered" });
        });
        if (size(newNotices) > 0) setNotices((prev) => [...prev, ...newNotices]);
        const hasNew = pending.some((order) => !previous[order.id]);
        if (hasNew) beep();
      }
      previousPendingRef.current = Object.fromEntries(
        pending.map((order) => [order.id, order])
      );
    } catch (error) {
      setOffline(true);
    }
  };

  // Al cambiar de área se vuelve a empezar: lo que ya estaba en la otra área
  // no es "nuevo" ni "cancelado" para esta.
  useEffect(() => {
    previousPendingRef.current = null;
    setNotices([]);
    load();
  }, [area]);

  // Cada 5 s y al volver a primer plano (tablet que se apagó y se prendió).
  usePolling(load, POLL_MS);

  // Los avisos se borran solos después de un rato.
  useEffect(() => {
    if (size(notices) === 0) return;
    const timeout = setTimeout(() => {
      const limit = Date.now() - NOTICE_VISIBLE_MS;
      setNotices((prev) => filter(prev, (item) => item.at > limit));
    }, 5000);
    return () => clearTimeout(timeout);
  }, [notices]);

  const onChangeArea = (value) => {
    setArea(value);
    try {
      localStorage.setItem(AREA_STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      // sin almacenamiento: el área solo dura mientras la pantalla esté abierta
    }
  };

  const onReady = async (orders) => {
    const ids = map(orders, "id");
    // Optimista: sale de la pantalla al toque, sin esperar al servidor.
    setOpenOrders((prev) =>
      map(prev, (order) =>
        ids.includes(order.id) ? { ...order, status: ORDER_STATUS.READY } : order
      )
    );
    if (previousPendingRef.current) {
      ids.forEach((id) => delete previousPendingRef.current[id]);
    }
    try {
      await Promise.all(ids.map((id) => markOrderReady(id)));
    } catch (error) {
      setOffline(true);
    }
    load();
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };

  const areaNames = uniq(
    compact(map(categories, (category) => (category.production_area || "").trim()))
  ).sort();
  const areaOptions = areaNames.map((name) => ({ key: name, value: name, text: name }));
  const allAreas = size(area) === 0;

  const pendingOrders = filter(
    openOrders,
    (order) => order.status === ORDER_STATUS.PENDING
  );
  const visibleOrders = filter(pendingOrders, (order) => inArea(order, area));
  const withoutArea =
    allAreas
      ? []
      : filter(pendingOrders, (order) => !normalizeArea(getOrderArea(order)));
  const tickets = buildTickets(visibleOrders);

  return (
    <div className="kitchen-screen">
      <HeaderPage title={allAreas ? "Cocina — todas las áreas" : `Cocina — ${area.join(" + ")}`} />

      <div className="kitchen-screen__toolbar">
        <Dropdown
          selection
          multiple
          clearable
          placeholder="Todas las áreas"
          options={areaOptions}
          value={area}
          onChange={(_, data) => onChangeArea(data.value)}
        />
        {soundOn ? (
          <span className="kitchen-screen__sound-on">
            <Icon name="volume up" /> Sonido activado
          </span>
        ) : (
          <Button color="orange" onClick={testBeep}>
            <Icon name="volume up" /> Activar sonido
          </Button>
        )}
        <Button basic onClick={toggleFullscreen} title="Pantalla completa">
          <Icon name="expand" /> Pantalla completa
        </Button>
      </div>

      {offline && (
        <Message warning>
          Sin conexión con el servidor — reintentando cada {POLL_MS / 1000} s.
          Los pedidos nuevos pueden no estar apareciendo.
        </Message>
      )}

      {size(withoutArea) > 0 && (
        <Message info size="small">
          Hay {size(withoutArea)} pedido(s) de categorías sin área asignada: se
          ven solo en "Todas las áreas" (selector vacío). Asígnales un área en Carta →
          Categorías.
        </Message>
      )}

      {size(notices) > 0 && (
        <div className="kitchen-screen__notices">
          {map(notices, ({ order, kind }) => (
            <div
              key={order.id}
              className={`kitchen-screen__notice kitchen-screen__notice--${kind}`}
            >
              <strong>
                {kind === "cancelled" ? "CANCELADO" : "YA ENTREGADO por el mozo"}
              </strong>{" "}
              · {getTablePlace(order)} ·{" "}
              <s>{order.product_data?.title || "Producto eliminado"}</s>
            </div>
          ))}
        </div>
      )}

      {openOrders === null ? (
        <p className="kitchen-screen__empty">Cargando pedidos…</p>
      ) : size(tickets) === 0 ? (
        <p className="kitchen-screen__empty">
          <Icon name="check circle outline" /> Sin pedidos pendientes
        </p>
      ) : (
        <div className="kitchen-screen__tickets">
          {map(tickets, (ticket) => {
            const minutes = Math.floor((now - ticket.firstTime) / 60000);
            return (
              <div
                key={ticket.key}
                className={classNames("kitchen-ticket", {
                  "kitchen-ticket--warn": minutes >= WARN_MINUTES && minutes < LATE_MINUTES,
                  "kitchen-ticket--late": minutes >= LATE_MINUTES,
                })}
              >
                <div className="kitchen-ticket__header">
                  <span className="kitchen-ticket__table">{ticket.place}</span>
                  <span className="kitchen-ticket__time">
                    {formatElapsed(ticket.firstTime, now)}
                  </span>
                </div>
                <div className="kitchen-ticket__by">
                  {ticket.orders[0].created_by_name || "Autoservicio (QR)"}
                </div>

                {map(ticket.lines, (lineOrders) => {
                  const order = lineOrders[0];
                  return (
                    <div key={order.id} className="kitchen-ticket__line">
                      <div className="kitchen-ticket__line-body">
                        <div className="kitchen-ticket__dish">
                          <span className="kitchen-ticket__qty">
                            {size(lineOrders)} ×
                          </span>{" "}
                          {order.product_data?.title || "Producto eliminado"}
                        </div>
                        {(allAreas || size(area) > 1) && getOrderArea(order) && (
                          <span className="kitchen-ticket__area">
                            {getOrderArea(order)}
                          </span>
                        )}
                        {order.takeaway && (
                          <div className="kitchen-ticket__takeaway">🥡 PARA LLEVAR</div>
                        )}
                        {order.offer_data && (
                          <div className="kitchen-ticket__offer">
                            🎁 {order.offer_data.title}
                          </div>
                        )}
                        {order.option_choice && (
                          <div className="kitchen-ticket__option">
                            {order.option_choice}
                          </div>
                        )}
                        {map(order.extras, (extra) => (
                          <span className="kitchen-ticket__extra" key={extra.id}>
                            + {extra.label} x{extra.quantity}
                          </span>
                        ))}
                        {order.note && (
                          <div className="kitchen-ticket__note">
                            <Icon name="sticky note outline" /> {order.note}
                          </div>
                        )}
                      </div>
                      {/* "Listo" marca UN plato (5 × → 4 ×): cocina puede ir
                          sacándolos de a uno. "Todos" marca la línea entera. */}
                      <div className="kitchen-ticket__buttons">
                        <Button
                          color="green"
                          className="kitchen-ticket__ready"
                          onClick={() => onReady([order])}
                        >
                          Listo
                        </Button>
                        {size(lineOrders) > 1 && (
                          <Button
                            basic
                            color="green"
                            className="kitchen-ticket__ready-all"
                            onClick={() => onReady(lineOrders)}
                          >
                            Todos ({size(lineOrders)})
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {size(ticket.lines) > 1 && (
                  <Button
                    fluid
                    color="green"
                    basic
                    className="kitchen-ticket__all-ready"
                    onClick={() => onReady(ticket.orders)}
                  >
                    Toda la comanda lista
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
